import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

export type LoginCredentials = {
  username: string
  password: string
}

type CredentialsDialogProps = {
  credentials: LoginCredentials
  onClose: () => void
}

/**
 * One-time display of the login credentials generated for a newly created
 * subcontractor employee. The password is shown only here (never re-fetchable),
 * so the admin can copy it or download it as a text file to hand to the worker.
 */
const CredentialsDialog: React.FC<CredentialsDialogProps> = ({ credentials, onClose }) => {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  // Clear any pending "copied" reset when the dialog unmounts so the timeout
  // does not fire setCopied on an unmounted component.
  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current)
      }
    }
  }, [])

  const asText = `${t('credentialsDialog.usernameLabel')}: ${credentials.username}\n${t(
    'credentialsDialog.passwordLabel',
  )}: ${credentials.password}`

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(asText)
      setCopied(true)
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current)
      }
      copiedTimeoutRef.current = setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy credentials to clipboard', err)
    }
  }

  const handleDownload = () => {
    const blob = new Blob([asText], { type: 'text/plain;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `credentials_${credentials.username}.txt`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('credentialsDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Alert severity="warning">{t('credentialsDialog.warning')}</Alert>
          <TextField
            label={t('credentialsDialog.usernameLabel')}
            value={credentials.username}
            InputProps={{ readOnly: true }}
            fullWidth
          />
          <TextField
            label={t('credentialsDialog.passwordLabel')}
            value={credentials.password}
            InputProps={{ readOnly: true }}
            fullWidth
          />
          {copied && (
            <Typography variant="body2" color="success.main">
              {t('credentialsDialog.copied')}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCopy}>{t('credentialsDialog.copy')}</Button>
        <Button onClick={handleDownload}>{t('credentialsDialog.download')}</Button>
        <Button variant="contained" onClick={onClose}>
          {t('credentialsDialog.close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CredentialsDialog
